import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer, Experience } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Briefcase, Plus, Trash2, Edit2, Check, X, Loader2, FileText, ExternalLink } from 'lucide-react';
import { FileUtils } from '@/utils/fileUtils';
import { fileService } from '@/api/files/file.service';

interface ExperienceInputProps {
  answer: Answer;
  onBlur: (value: { experienceValue: Experience[] }) => void;
  disabled?: boolean;
}

export function ExperienceInput({ answer, onBlur, disabled }: ExperienceInputProps) {
  const [experiences, setExperiences] = useState<Experience[]>(answer.experienceValue || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [formData, setFormData] = useState<Partial<Experience>>({
    company: '',
    position: '',
    startDate: '',
    endDate: '',
    current: false,
    description: '',
  });

  const handleSubmit = () => {
    if (!formData.company || !formData.position || !formData.startDate) return;

    let newExperiences: Experience[];
    if (editingIndex !== null) {
      newExperiences = [...experiences];
      newExperiences[editingIndex] = formData as Experience;
    } else {
      newExperiences = [...experiences, formData as Experience];
    }

    setExperiences(newExperiences);
    onBlur({ experienceValue: newExperiences });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
    resetForm();
  };

  const handleDelete = (index: number) => {
    const newExperiences = experiences.filter((_, i) => i !== index);
    setExperiences(newExperiences);
    onBlur({ experienceValue: newExperiences });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setFormData(experiences[index]);
    setIsAdding(true);
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormData({
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
      contactPhone: '',
      supervisorName: '',
      companyNit: '',
      workSchedule: '',
      hasCertificate: undefined,
      certificateFileId: undefined,
      certificateName: undefined,
      certificateUrl: undefined,
      certificateMimeType: undefined,
      certificateSize: undefined,
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing[4], gap: spacing[2] }}>
        <div>
          <label style={{ fontSize: 16, fontWeight: 600, color: colors.gray[900], display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
            <Briefcase size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>}
          </label>
          {answer.question.description && <p style={{ fontSize: 14, color: colors.gray[600] }}>{answer.question.description}</p>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          <AnimatePresence>
            {isSaved && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                style={{ display: 'flex', alignItems: 'center', gap: spacing[1], color: colors.success?.[500] || '#10b981', fontSize: 12, fontWeight: 600 }}
              >
                <Check size={14} />
                <span>Guardado</span>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={() => setIsAdding(true)}
            disabled={disabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[1],
              padding: `${spacing[2]} ${spacing[3]}`,
              backgroundColor: colors.primary[500],
              color: colors.white,
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <Plus size={14} />
            Agregar
          </motion.button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        {experiences.map((exp, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ padding: spacing[4], backgroundColor: colors.gray[50], borderRadius: 14, border: `1px solid ${colors.gray[200]}` }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing[2], gap: spacing[2] }}>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.gray[900] }}>{exp.position}</h4>
                <p style={{ fontSize: 13, color: colors.primary[600], fontWeight: 600 }}>{exp.company}</p>
              </div>
              <div style={{ display: 'flex', gap: spacing[1] }}>
                <button onClick={() => handleEdit(index)} style={{ padding: spacing[1], background: 'transparent', border: 'none', color: colors.gray[500], cursor: 'pointer' }}>
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(index)} style={{ padding: spacing[1], background: 'transparent', border: 'none', color: colors.error?.[500] || '#ef4444', cursor: 'pointer' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <p style={{ fontSize: 12, color: colors.gray[500], marginBottom: spacing[2] }}>
              {exp.startDate} - {exp.current ? 'Actual' : exp.endDate}
            </p>
            {exp.contactPhone && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>Teléfono:</strong> {exp.contactPhone}</p>}
            {exp.supervisorName && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>Supervisor:</strong> {exp.supervisorName}</p>}
            {exp.companyNit && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>NIT:</strong> {exp.companyNit}</p>}
            {exp.workSchedule && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>Horario:</strong> {exp.workSchedule}</p>}
            {exp.hasCertificate !== undefined && <p style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1] }}><strong>Certificado:</strong> {exp.hasCertificate ? 'Sí' : 'No'}</p>}
            <p style={{ fontSize: 13, color: colors.gray[700], lineHeight: 1.5 }}>{exp.description}</p>
          </motion.div>
        ))}

        {experiences.length === 0 && !isAdding && (
          <div style={{ padding: spacing[6], backgroundColor: colors.gray[50], borderRadius: 14, border: `1px dashed ${colors.gray[300]}`, textAlign: 'center' }}>
            <Briefcase size={28} color={colors.gray[400]} style={{ marginBottom: spacing[2] }} />
            <p style={{ fontSize: 13, color: colors.gray[600] }}>No has agregado experiencia laboral aun</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{
              marginTop: spacing[4],
              padding: spacing[4],
              backgroundColor: colors.gray[50],
              borderRadius: 16,
              border: `1px solid ${colors.primary[300]}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing[4] }}>
              <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.primary[700] }}>{editingIndex !== null ? 'Editar experiencia' : 'Nueva experiencia'}</h4>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={16} color={colors.gray[500]} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Empresa *</label>
                <input type="text" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="Nombre de empresa" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Cargo *</label>
                <input type="text" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="Posicion" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Fecha inicio *</label>
                <input type="date" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Fecha fin</label>
                <input type="date" value={formData.endDate || ''} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} disabled={formData.current} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14, opacity: formData.current ? 0.5 : 1 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: spacing[1], fontSize: 13, color: colors.gray[700] }}>
                  <input type="checkbox" checked={formData.current} onChange={(e) => setFormData({ ...formData, current: e.target.checked, endDate: '' })} />
                  Trabajo actual
                </label>
              </div>
            </div>

            <div style={{ marginBottom: spacing[4] }}>
              <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Descripcion</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14, minHeight: 78, resize: 'vertical' }} placeholder="Describe responsabilidades y logros" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Teléfono de contacto</label>
                <input type="tel" value={formData.contactPhone || ''} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="+57 301 234 5678" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Nombre del supervisor</label>
                <input type="text" value={formData.supervisorName || ''} onChange={(e) => setFormData({ ...formData, supervisorName: e.target.value })} style={{ width: '100%', padding:  spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="María González" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[4] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>NIT de la empresa</label>
                <input type="text" value={formData.companyNit || ''} onChange={(e) => setFormData({ ...formData, companyNit: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="901.234.567-8" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Horario laboral</label>
                <input type="text" value={formData.workSchedule || ''} onChange={(e) => setFormData({ ...formData, workSchedule: e.target.value })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }} placeholder="Lunes a Viernes 8:00 AM - 5:00 PM" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: spacing[3], marginBottom: spacing[3] }}>
              <div>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>¿Tienes certificado?</label>
                <select value={formData.hasCertificate === undefined ? '' : formData.hasCertificate.toString()} onChange={(e) => setFormData({ ...formData, hasCertificate: e.target.value === 'true' })} style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }}>
                  <option value="">Seleccionar...</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            {formData.hasCertificate && (
              <div style={{ marginBottom: spacing[4] }}>
                <label style={{ fontSize: 12, color: colors.gray[600], marginBottom: spacing[1], display: 'block' }}>Certificado</label>

                {formData.certificateUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[200]}`, backgroundColor: colors.white, marginBottom: spacing[2] }}>
                    <FileText size={16} color={colors.primary[600]} />
                    <span style={{ flex: 1, fontSize: 13, color: colors.gray[800] }}>{formData.certificateName}</span>
                    <a href={formData.certificateUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, color: colors.primary[600], fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                      <ExternalLink size={12} />
                      Ver
                    </a>
                  </div>
                )}

                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={uploadingCert}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingCert(true);
                    try {
                      const base64 = await FileUtils.fileToBase64(file);
                      const uploaded = await fileService.uploadBase64({
                        fileName: file.name,
                        mimeType: file.type || 'application/octet-stream',
                        base64,
                        folder: 'workers/documents',
                      });
                      setFormData({
                        ...formData,
                        certificateFileId: uploaded.id,
                        certificateName: uploaded.originalName || file.name,
                        certificateUrl: uploaded.publicUrl,
                        certificateMimeType: uploaded.mimeType || file.type,
                        certificateSize: Number(uploaded.size) || file.size,
                      });
                    } catch (err) {
                      console.error('Error uploading certificate:', err);
                      alert('Error al subir el certificado');
                    } finally {
                      setUploadingCert(false);
                    }
                  }}
                  style={{ width: '100%', padding: spacing[2], borderRadius: 8, border: `1px solid ${colors.gray[300]}`, fontSize: 14 }}
                />
                {uploadingCert && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing[1], marginTop: spacing[1], color: colors.primary[600], fontSize: 12, fontWeight: 500 }}>
                    <Loader2 size={12} />
                    Subiendo certificado...
                  </div>
                )}
                <p style={{ fontSize: 11, color: colors.gray[500], marginTop: spacing[1] }}>Formatos permitidos: PDF, JPG, PNG. Máximo 5MB</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing[2] }}>
              <button onClick={resetForm} style={{ padding: `${spacing[2]} ${spacing[3]}`, backgroundColor: 'transparent', border: `1px solid ${colors.gray[300]}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={handleSubmit} style={{ padding: `${spacing[2]} ${spacing[3]}`, backgroundColor: colors.primary[500], color: colors.white, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {editingIndex !== null ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
